// Notification action handler. Mounts once at app startup (inside the
// router so navigate() is available) and routes notification taps +
// action buttons to the right destination.
//
// Wiring:
//   - Tapping notification body         -> open app + /dashboard?logDose=<componentId>
//   - "Log Now" action button           -> open app + /dashboard?logDose=<componentId>
//   - "Skip" action button              -> background skip via skipDoseFromNotification(),
//                                          no app foreground, follow-up confirmation toast
//                                          fires if the listener happens to run while the
//                                          app is already in the foreground.

import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  isNative,
  registerNotificationActionTypes,
  NOTIFICATION_ACTION_SKIP,
} from './native';
import { skipDoseFromNotification } from '../data/skipFromNotification';
import { useToast } from '../components/Toast';

export function useNotificationActions(): void {
  const navigate = useNavigate();
  const { showToast } = useToast();

  useEffect(() => {
    if (!isNative()) return;

    let pluginListenerRemove: (() => Promise<void>) | null = null;
    let cancelled = false;

    (async () => {
      try {
        // Register the action types with iOS first so the notifications
        // we already have queued know what buttons to show.
        await registerNotificationActionTypes();

        const { LocalNotifications } = await import('@capacitor/local-notifications');
        const handle = await LocalNotifications.addListener(
          'localNotificationActionPerformed',
          async (event) => {
            const extra = (event.notification?.extra ?? {}) as {
              type?: string;
              componentId?: string;
              protocolId?: string;
            };
            if (extra.type !== 'dose_reminder' || !extra.componentId) return;

            // actionId === 'tap' means the user tapped the notification body
            // (no button). Treat that the same as Log Now - open the modal.
            const actionId = event.actionId;
            if (actionId === NOTIFICATION_ACTION_SKIP) {
              // Skip is registered as foreground:false, so iOS does NOT
              // bring the app forward. Perform the skip directly in
              // the listener — no navigate() needed. If the app
              // happens to be in the foreground when the user taps
              // Skip from notification center, surface a toast so
              // they get visual confirmation.
              const result = await skipDoseFromNotification(extra.componentId);
              if (result.ok) {
                showToast(`${result.peptideName} dose skipped`, 'success');
              } else if (result.reason === 'already_logged') {
                showToast("That dose is already logged.", 'error');
              } else if (result.reason === 'not_found') {
                showToast("That dose isn't on today's schedule anymore.", 'error');
              } else {
                showToast("Couldn't skip that dose. Try again.", 'error');
              }
              return;
            }
            // Log Now / tap — open the app and navigate to the modal.
            navigate(`/dashboard?logDose=${extra.componentId}`);
          },
        );
        if (cancelled) {
          await handle.remove();
        } else {
          pluginListenerRemove = () => handle.remove();
        }
      } catch (err) {
        console.error('[useNotificationActions] setup failed:', err);
      }
    })();

    return () => {
      cancelled = true;
      if (pluginListenerRemove) {
        void pluginListenerRemove();
      }
    };
  }, [navigate, showToast]);
}
